import React from 'react'
import useTrans from '@hooks/useTrans'
import { PosizionePersonalizzazioneOrdinePdfType } from '@utils/types'
import { cn } from '@utils/safeTs'
import { WImage } from '@components/atoms'
import styles from './PdfBozzaEditor.module.sass'
import { Image } from '@components/icons'

interface Props {
  posizione?: PosizionePersonalizzazioneOrdinePdfType
  fromAdmin?: boolean
  className?: string
}

const PdfBozzaEditor = (props: Props) => {
  const t = useTrans()

  const {
    posizione,
    className = '',
  } = props

  if (!posizione) return <></>

  const righeOrdine = posizione?.personalizzazione?.righeOrdine || []
  const mainArticle = righeOrdine?.[0]?.articolo
  const gridSize = righeOrdine.length > 4 ? righeOrdine.length > 8 ? 'sm' : 'md' : 'lg'
  const printLabel = posizione?.numeroColoriPosizione?.fullColor
    ? posizione?.numeroColoriPosizione?.nome
    : posizione?.numeroColoriPosizione?.numeroColori || undefined
  const pantoniLabel = posizione?.pantoni?.map((pantone) => pantone?.pantone).filter(Boolean)
  const metaParts = [
    { key: 'tecnica', value: posizione?.tecnica?.nome },
    { key: 'posizione', label: t('Posizione'), value: posizione.posizione?.nome },
    { key: 'area', label: t('Area stampa'), value: posizione.larghezza && posizione.altezza ? `${posizione.larghezza} x ${posizione.altezza} cm` : undefined },
  ].filter(x => !!x.value)

  return (
    <div className={cn(styles.root, styles[`root--${gridSize}`], className)}>
      <div className={styles.head}>
        <div className={styles.codeBox}>
          <p className={styles.codeLabel}>{t('Cod.')}</p>
          <p className={styles.codeValue}>{mainArticle?.codice || '-'}</p>
        </div>
        <div className={styles.headContent}>
          <h1 className={styles.title}>{mainArticle?.titolo}</h1>
          {!!metaParts.length && (
            <p className={styles.meta}>
              {metaParts.map(x => (
                <span key={x.key}>
                  {!!x.label && <span>{x.label}{' '}</span>}
                  <span className={styles.metaValue}>{x.value}</span>
                </span>
              ))}
            </p>
          )}
        </div>
      </div>

      <div className={styles.colorsBar}>
        <div className={styles.colorsList}>
          <p className={styles.colorsLabel}>{t('Colori scelti:')}</p>
          {righeOrdine.map((riga) => (
            <span key={riga.id} className={styles.colorTag}>
              <span className={styles.colorImage}>
                <WImage src={riga.articolo?.colore?.image?.url} maxWidth={10} maxHeight={10} objectFit='cover' />
              </span>
              <span>{riga.articolo?.colore?.nome}</span>
            </span>
          ))}
        </div>

        {(!!printLabel || !!pantoniLabel?.length) && (
          <p className={styles.printInfo}>
            {printLabel ? (
              <span>
                <span>{t('Stampa')}{': '}</span>
                <span className={styles.printInfoValue}>{printLabel}</span>
              </span>
            ) : null}
            {pantoniLabel?.length ? (
              <span>
                <span>{t(pantoniLabel?.length > 1 ? 'Pantoni' : 'Pantone')}{': '}</span>
                <span className={styles.printInfoValue}>{pantoniLabel.join(" / ")}</span>
              </span>
            ) : null}
          </p>
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.thumbnails}>
          {righeOrdine.map((riga, index) => (
            <div key={riga.id} className={styles.thumbnailItem}>
                {riga.articolo?.mainImage?.url ? (
                  <WImage
                    next={false}
                    src={riga.articolo?.mainImage?.url}
                    maxWidth={80}
                    maxHeight={80}
                    objectFit='contain'
                    alt={t('Anteprima {0}', [riga.articolo.titolo])}
                    className={styles.thumbnailImage}
                  />
                ) : (
                  <div className={styles.thumbnailPlaceholder}>
                    <Image />
                  </div>
                )}
            </div>
          ))}
        </div>

        <div className={styles.preview}>
          <div className={styles.previewFrame}>
            {mainArticle?.mainImage?.url ? (
              <WImage
                next={false}
                src={mainArticle.mainImage?.url}
                maxWidth={630}
                maxHeight={630}
                objectFit='contain'
                alt={t('Bozza digitale {0}', [mainArticle?.titolo || ''])}
                className={styles.previewImage}
              />
            ) : (
              <div className={styles.previewPlaceholder}>{t('Anteprima non disponibile')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PdfBozzaEditor
